import { useEffect, useState } from 'react'

import { Dropdown, type DropdownOption } from '@/components/ui'
import { useApi } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import type { Asset, InsightParametersType } from '@/types/dashboard-types'

const dateOptions: DropdownOption[] = [
  { label: 'Insight Default', value: '' },
  ...DateOptions.filter((o) => o.value !== ''),
]

interface TopNavDashboardControlsProps {
  parameters: InsightParametersType
  onSave: (parameters: InsightParametersType) => void
}

/** Shared hook that manages control state, asset loading, and save logic */
export const useDashboardControls = ({ parameters, onSave }: TopNavDashboardControlsProps) => {
  const { companies } = useCompany()
  const { api } = useApi()

  const [companyId, setCompanyId] = useState<number | undefined>(
    parameters.companyId ? Number(parameters.companyId) : undefined
  )
  const [assetId, setAssetId] = useState<string | undefined>(
    parameters.assetId ? String(parameters.assetId) : undefined
  )
  const [date, setDate] = useState<string>(parameters.date ? String(parameters.date) : '')
  const [from, setFrom] = useState<string>(parameters.from ? String(parameters.from) : '')
  const [to, setTo] = useState<string>(parameters.to ? String(parameters.to) : '')
  const [assetOptions, setAssetOptions] = useState<DropdownOption[]>([])

  // Sync from props when parameters change externally
  useEffect(() => {
    setCompanyId(parameters.companyId ? Number(parameters.companyId) : undefined)
    setAssetId(parameters.assetId ? String(parameters.assetId) : undefined)
    setDate(parameters.date ? String(parameters.date) : '')
    setFrom(parameters.from ? String(parameters.from) : '')
    setTo(parameters.to ? String(parameters.to) : '')
  }, [parameters.companyId, parameters.assetId, parameters.date, parameters.from, parameters.to])

  // Load assets when company changes
  useEffect(() => {
    if (!companyId || !api) {
      setAssetOptions([])
      return
    }
    const fn = async () => {
      const response = await api.get<Asset[]>({
        path: `/assets?companyId=${companyId}&skip=0&take=10000&deleted=false`,
        origin: 'TopNavDashboardControls',
        expiresIn: '1h',
      })
      if (!response.error && response.data) {
        setAssetOptions(
          response.data.map((a) => ({
            label: a.name,
            value: String(a.id),
          }))
        )
      }
    }
    fn()
  }, [companyId, api])

  const hasChanges =
    companyId !== (parameters.companyId ? Number(parameters.companyId) : undefined) ||
    assetId !== (parameters.assetId ? String(parameters.assetId) : undefined) ||
    date !== (parameters.date ? String(parameters.date) : '') ||
    from !== (parameters.from ? String(parameters.from) : '') ||
    to !== (parameters.to ? String(parameters.to) : '')

  const handleSave = () => {
    const params: InsightParametersType = {}
    if (companyId) params.companyId = companyId
    if (assetId) params.assetId = assetId
    if (date) params.date = date
    if (date === 'custom') {
      if (from) params.from = from
      if (to) params.to = to
    }
    params.showInNav = true
    onSave(params)
  }

  const companyOptions: DropdownOption[] = [
    { label: 'All Companies', value: '' },
    ...companies.map((c) => ({ label: c.name, value: String(c.id) })),
  ]

  const showCompany = companies.length > 1 && !!parameters.companyId
  const showAsset = !!companyId && !!parameters.assetId
  const showDate = !!parameters.date

  // Build summary text
  const summaryParts: string[] = []
  if (showCompany) {
    const company = companies.find((c) => String(c.id) === String(parameters.companyId))
    if (company) summaryParts.push(company.name)
  }
  if (showAsset && parameters.assetId) {
    const asset = assetOptions.find((a) => a.value === String(parameters.assetId))
    summaryParts.push(asset?.label || String(parameters.assetId))
  }
  if (showDate) {
    if (parameters.date === 'custom' && (parameters.from || parameters.to)) {
      const formatDt = (v: string | undefined) => {
        if (!v) return '...'
        const s = String(v)
        const time = s.slice(11, 16)
        if (!time || time === '00:00' || time === '23:59') return s.slice(0, 10)
        return `${s.slice(0, 10)} ${time}`
      }
      summaryParts.push(`${formatDt(parameters.from as string)} – ${formatDt(parameters.to as string)}`)
    } else {
      const opt = dateOptions.find((o) => o.value === parameters.date)
      if (opt) summaryParts.push(opt.label)
    }
  }
  const summaryText = summaryParts.join('  |  ')

  // Count how many control groups are active (for xl breakpoint logic)
  const controlCount = [showCompany || showAsset, showDate].filter(Boolean).length

  return {
    companyId,
    setCompanyId,
    assetId,
    setAssetId,
    date,
    setDate,
    from,
    setFrom,
    to,
    setTo,
    assetOptions,
    companyOptions,
    showCompany,
    showAsset,
    showDate,
    hasChanges,
    handleSave,
    summaryText,
    controlCount,
  }
}

type DashboardControlsReturn = ReturnType<typeof useDashboardControls>

/** Renders the dropdown controls in either horizontal or vertical layout */
export const ControlsForm = ({
  controls,
  layout = 'horizontal',
  hideApply = false,
}: {
  controls: DashboardControlsReturn
  layout?: 'horizontal' | 'vertical'
  hideApply?: boolean
}) => {
  const {
    companyId,
    setCompanyId,
    assetId,
    setAssetId,
    date,
    setDate,
    from,
    setFrom,
    to,
    setTo,
    assetOptions,
    companyOptions,
    showCompany,
    showAsset,
    showDate,
    hasChanges,
    handleSave,
  } = controls

  if (layout === 'vertical') {
    return (
      <div className="space-y-3">
        {showCompany && (
          <div>
            <label className="text-xs text-base-content/60 mb-1 block">Company</label>
            <Dropdown
              options={companyOptions}
              selectedItem={companyOptions.find((o) => Number(o.value) === companyId) || companyOptions[0]}
              handleSingleSelectItem={(opt: DropdownOption | null) => {
                const newId = opt?.value ? Number(opt.value) : undefined
                setCompanyId(newId)
                setAssetId(undefined)
              }}
              placeholder="Company..."
            />
          </div>
        )}

        {showAsset && (
          <div>
            <label className="text-xs text-base-content/60 mb-1 block">Asset</label>
            <Dropdown
              options={[{ label: 'All Assets', value: '' }, ...assetOptions]}
              selectedItem={assetOptions.find((o) => o.value === assetId) || { label: 'All Assets', value: '' }}
              handleSingleSelectItem={(opt: DropdownOption | null) => setAssetId(opt?.value || undefined)}
              placeholder="Asset..."
            />
          </div>
        )}

        {showDate && (
          <div>
            <label className="text-xs text-base-content/60 mb-1 block">Date</label>
            <Dropdown
              options={dateOptions}
              selectedItem={dateOptions.find((o) => o.value === date) || dateOptions[0]}
              handleSingleSelectItem={(opt: DropdownOption | null) => {
                setDate(opt?.value || '')
                if (opt?.value !== 'custom') {
                  setFrom('')
                  setTo('')
                }
              }}
              placeholder="Date..."
              isClearable={false}
            />
            {date === 'custom' && (
              <div className="flex gap-2 mt-2">
                <div className="flex-1">
                  <label className="text-xs text-base-content/60 mb-1 block">From</label>
                  <input
                    type="datetime-local"
                    className="input input-sm w-full"
                    value={from ? from.slice(0, 16) : ''}
                    onChange={(e) => setFrom(e.target.value || '')}
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs text-base-content/60 mb-1 block">To</label>
                  <input
                    type="datetime-local"
                    className="input input-sm w-full"
                    value={to ? to.slice(0, 16) : ''}
                    onChange={(e) => setTo(e.target.value || '')}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleSave}
          className={`btn btn-sm btn-primary w-full ${hasChanges ? '' : 'btn-disabled'}`}
          disabled={!hasChanges}
        >
          Apply
        </button>
      </div>
    )
  }

  // Horizontal layout
  return (
    <div className="-mt-[9px]">
      {/* Labels row */}
      <div className="flex items-center gap-3 mb-[3px] justify-end">
        {showCompany && (
          <span className="text-[10px] leading-none text-primary-content/60 min-w-0 max-w-[350px] flex-[3]">
            Company
          </span>
        )}
        {showAsset && <span className="text-[10px] leading-none text-primary-content/60 min-w-0 flex-[2]">Asset</span>}
        {showDate && (
          <>
            <span className="text-[10px] leading-none text-primary-content/60 min-w-0 max-w-[200px] flex-[2]">
              Date
            </span>
            {date === 'custom' && (
              <>
                <span className="text-[10px] leading-none text-primary-content/60 min-w-0 max-w-[200px] flex-[1.5]">
                  From
                </span>
                <span className="text-[10px] leading-none text-primary-content/60 min-w-0 max-w-[200px] flex-[1.5]">
                  To
                </span>
              </>
            )}
          </>
        )}
      </div>
      {/* Controls row */}
      <div className="flex items-center gap-3 justify-end">
        {showCompany && (
          <div className="min-w-0 max-w-[350px] flex-[3]">
            <Dropdown
              options={companyOptions}
              selectedItem={companyOptions.find((o) => Number(o.value) === companyId) || companyOptions[0]}
              handleSingleSelectItem={(opt: DropdownOption | null) => {
                const newId = opt?.value ? Number(opt.value) : undefined
                setCompanyId(newId)
                setAssetId(undefined)
              }}
              placeholder="Company..."
              isClearable={false}
            />
          </div>
        )}

        {showAsset && (
          <div className="min-w-0 flex-[2]">
            <Dropdown
              options={[{ label: 'All Assets', value: '' }, ...assetOptions]}
              selectedItem={assetOptions.find((o) => o.value === assetId) || { label: 'All Assets', value: '' }}
              handleSingleSelectItem={(opt: DropdownOption | null) => setAssetId(opt?.value || undefined)}
              placeholder="Asset..."
              isClearable={false}
            />
          </div>
        )}

        {showDate && (
          <>
            <div className="min-w-0 max-w-[200px] flex-[2]">
              <Dropdown
                options={dateOptions}
                selectedItem={dateOptions.find((o) => o.value === date) || dateOptions[0]}
                handleSingleSelectItem={(opt: DropdownOption | null) => {
                  setDate(opt?.value || '')
                  if (opt?.value !== 'custom') {
                    setFrom('')
                    setTo('')
                  }
                }}
                placeholder="Date..."
                isClearable={false}
              />
            </div>
            {date === 'custom' && (
              <>
                <input
                  type="datetime-local"
                  className="input input-sm min-w-0 max-w-[200px] flex-[1.5] text-base-content bg-base-100"
                  value={from ? from.slice(0, 16) : ''}
                  onChange={(e) => setFrom(e.target.value || '')}
                />
                <input
                  type="datetime-local"
                  className="input input-sm min-w-0 max-w-[200px] flex-[1.5] text-base-content bg-base-100"
                  value={to ? to.slice(0, 16) : ''}
                  onChange={(e) => setTo(e.target.value || '')}
                />
              </>
            )}
          </>
        )}

        {!hideApply && (
          <button
            onClick={handleSave}
            className={`btn btn-sm btn-ghost text-primary-content border-primary-content/30 hover:bg-primary-content/10 ${hasChanges ? '' : 'invisible'}`}
          >
            Apply
          </button>
        )}
      </div>
    </div>
  )
}

/** Read-only summary of current control values */
export const ControlsSummary = ({ controls }: { controls: DashboardControlsReturn }) => {
  if (!controls.summaryText) return null
  return <span className="text-sm text-primary-content/80 truncate max-w-[400px]">{controls.summaryText}</span>
}

/** Original component kept for backwards compatibility - now uses the extracted pieces */
export const TopNavDashboardControls = ({ parameters, onSave }: TopNavDashboardControlsProps) => {
  const controls = useDashboardControls({ parameters, onSave })
  return <ControlsForm controls={controls} layout="horizontal" />
}
